/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { ToolbarComponentCommonProps } from '..';
import { useAppSelector } from '../../../../reducers';
import { AnalyticsEvents } from '../../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../../services/analytics/AnalyticsService';
import { setOverviewMode, SurveyOverviewMode } from '../../../../slice/surveySettings';
import SimpleTooltip from '../../../simple-tooltip';
import Select, { ActionMeta, components, OptionProps, SingleValue } from 'react-select';
import { QUESTION } from '../../../../constants/terminology';

export interface ScopeView {
  value: number;
  label: string;
  description: string;
}

const scopeViews: ScopeView[] = [
  {
    value: SurveyOverviewMode.ScopeGroups,
    label: 'Survey packs',
    description:
      `This the default view. Groups of ${QUESTION.PLURAL} follow the scope selected. ${QUESTION.CAPITALIZED_PLURAL} may appear in multiple groups.`,
  },
  {
    value: SurveyOverviewMode.QuestionList,
    label: 'Distinct list',
    description: `A list of unique ${QUESTION.PLURAL}`,
  },
  {
    value: SurveyOverviewMode.SdgAndScope,
    label: 'SDG aligned packs',
    description:
      `A combined view that picks out key Sustainable Development Goal aligned ${QUESTION.PLURAL} into groupings, with the remainder of the ${QUESTION.PLURAL} following the scope selected. ${QUESTION.CAPITALIZED_PLURAL} assigned in the SDG groups will not appear in the respective scope groups.`,
  },
];

const Option = (props: OptionProps<ScopeView, false>) => {
  return (
    <components.Option {...props}>
      <div className='strong'>{props.data.label}</div>
      <div className='pt-1 pb-2 '>{props.data.description}</div>
    </components.Option>
  );
};

export const ScopeViewDropdown = (props: ToolbarComponentCommonProps) => {
  const { initiativeId, surveyId, defaultOverviewMode, handleSelectOverviewMode } = props;

  const dispatch = useDispatch();
  const surveyOverviewMode = useAppSelector((state) => state.surveySettings.overviewMode);

  const selectView = async (view: ScopeView) => {
    dispatch(setOverviewMode(view.value));
    const analytics = getAnalytics();
    if (surveyId) {
      analytics.track(AnalyticsEvents.SurveyGroupView, {
        initiativeId,
        surveyId,
        mode: view.value,
        modeLabel: view.label,
      });
    }
  };

  const selectedSort = scopeViews.find((s) => s.value === (defaultOverviewMode ?? surveyOverviewMode)) ?? scopeViews[0];

  const handleSelect = (t: SingleValue<ScopeView>, actionMeta: ActionMeta<ScopeView>) => {
    if (!t) {
      return;
    }

    if (handleSelectOverviewMode) {
      handleSelectOverviewMode(t);
    } else {
      selectView(t);
    }
  };

  return (
    <>
      <span className='ml-2 mr-2'>Group by:</span>
      <SimpleTooltip text={'Click to choose view mode'}>
        <Select
          isClearable={false}
          isSearchable={false}
          options={scopeViews}
          onChange={handleSelect}
          defaultValue={selectedSort}
          components={{ Option }}
          isMulti={false}
          styles={{
            menu: (baseStyles) => ({
              ...baseStyles,
              width: '300px',
              whiteSpace: 'normal',
            }),
            dropdownIndicator: (baseStyles) => ({
              ...baseStyles,
              padding: '4px',
            }),
            control: (baseStyles) => ({
              ...baseStyles,
              minHeight: '35px',
              borderColor: '#e6e9ed',
              minWidth: '175px',
            }),
          }}
        />
      </SimpleTooltip>
    </>
  );
};
